import React from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  ButtonProps,
  FormField,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsExportPreset } from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { presetScopeLabel } from "./omsPresetEditing";

export default function OMSPresetToolbar({
  state,
  applied,
  disabled,
  saveDisabled = disabled,
  onApply,
  onSave,
  onManage,
  saveRef,
  selectRef,
  manageRef,
  modified = false,
  onCustom,
  onReset,
  onUndo,
  onEdit,
  onReview,
  editing = false,
}: {
  state: OmsPresetsState;
  applied?: OmsExportPreset;
  disabled: boolean;
  saveDisabled?: boolean;
  onApply: (presetId: string) => void;
  onSave: () => void;
  onManage: () => void;
  saveRef: React.RefObject<ButtonProps.Ref>;
  selectRef: React.RefObject<SelectProps.Ref>;
  manageRef: React.RefObject<ButtonDropdownProps.Ref>;
  modified?: boolean;
  onCustom?: () => void;
  onReset?: () => void;
  onUndo?: () => void;
  onEdit?: () => void;
  onReview?: () => void;
  editing?: boolean;
}) {
  return (
    <SpaceBetween size="s">
      <div className="oms-preset-toolbar">
        <FormField label="Saved preset (optional)">
          <Select
            expandToViewport
            ref={selectRef}
            ariaLabel="Saved preset"
            placeholder="Choose a preset"
            filteringType="auto"
            filteringPlaceholder="Search presets"
            filteringAriaLabel="Search presets"
            filteringClearAriaLabel="Clear preset search"
            empty="No saved presets for this product."
            noMatch="No matching presets."
            statusType={
              state.list.isLoading || state.read.isLoading
                ? "loading"
                : "finished"
            }
            loadingText={
              state.read.isLoading ? "Applying preset" : "Loading presets"
            }
            disabled={disabled || state.read.isLoading}
            selectedOption={
              applied
                ? {
                    value: applied.presetId,
                    label: applied.name,
                    description: presetScopeLabel[applied.scope],
                  }
                : null
            }
            options={(state.list.data || []).map((preset) => ({
              value: preset.presetId,
              label: preset.name,
              description: presetScopeLabel[preset.scope],
            }))}
            onChange={({ detail }) =>
              onApply(detail.selectedOption.value as string)
            }
          />
        </FormField>
        <div className="oms-preset-toolbar__actions">
          {modified && <Badge>Modified</Badge>}
          {(!applied || modified || editing) && (
            <Button
              ref={saveRef}
              disabled={saveDisabled || state.read.isLoading}
              onClick={applied && (modified || editing) ? onReview : onSave}
            >
              {applied && (modified || editing)
                ? "Review changes"
                : "Save preset"}
            </Button>
          )}
          <ButtonDropdown
            ref={manageRef}
            expandToViewport
            ariaLabel="Settings actions"
            disabled={disabled || state.read.isLoading}
            items={[
              ...(applied
                ? [
                    { id: "custom", text: "Custom settings" },
                    { id: "copy", text: "Save as new preset" },
                    { id: "edit", text: "Edit preset" },
                  ]
                : []),
              { id: "manage", text: "Manage presets" },
              { id: "reset", text: "Reset settings" },
              ...(onUndo ? [{ id: "undo", text: "Undo settings change" }] : []),
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === "custom") onCustom?.();
              else if (detail.id === "copy") onSave();
              else if (detail.id === "edit") onEdit?.();
              else if (detail.id === "reset") onReset?.();
              else if (detail.id === "undo") onUndo?.();
              else onManage();
            }}
          >
            More actions
          </ButtonDropdown>
        </div>
      </div>
      {state.list.isError && (
        <Alert
          type="warning"
          header="Presets unavailable"
          action={
            <Button
              onClick={() => {
                state.list.refetch().catch(() => undefined);
              }}
            >
              Retry presets
            </Button>
          }
        >
          You can still configure and create an export.
        </Alert>
      )}
      {state.read.isError && (
        <Alert
          type="error"
          header="Could not apply preset"
          action={
            <Button
              onClick={() => {
                if (state.read.variables) onApply(state.read.variables);
              }}
            >
              Retry preset
            </Button>
          }
        >
          {presetErrorMessage(state.read.error)}
        </Alert>
      )}
    </SpaceBetween>
  );
}
